import React, { useEffect } from 'react';
import './ProfileSearch.scss';

import emptyIllustration from './img/empty.png';
import { thumbImgProps, fallbackToOriginal } from "../../utils/imageUrl";

import {
  HARD_TTL,
  cacheGetStale,
  cacheSet,
} from '../../utils/swrcache';

const DEFAULT_AVATAR_URL = 'https://cvanto-photos.s3.us-east-1.amazonaws.com/DefaultAvatar/avatar.png';

const TTL_5MIN = 5 * 60 * 1000;
const TTL_24H = 24 * 60 * 60 * 1000;

const posterThemes = [
  'blue',
  'violet',
  'graphite',
  'green',
  'bronze',
  'wine',
  'ocean',
  'sand',
];

const getStableTheme = (item, title) => {
  const source = String(item?.listingId || item?.id || title || 'profile-search');
  let hash = 0;

  for (let i = 0; i < source.length; i += 1) {
    hash = source.charCodeAt(i) + ((hash << 5) - hash);
  }

  return posterThemes[Math.abs(hash) % posterThemes.length];
};

const getTitleSizeClass = (title) => {
  const len = String(title || '').trim().length;

  if (len <= 18) return 'is-short';
  if (len <= 42) return 'is-medium';
  return 'is-long';
};

export default function ProfileSearch({
  listings,
  isStaff = false,
  deepItem,
  onOpenListing,
  userId,
}) {
  const cacheKey = userId ? `profile:listings:${userId}` : null;

  const hydrated = (() => {
    if (!cacheKey) return null;
    const c = cacheGetStale(cacheKey, TTL_5MIN);
    return Array.isArray(c) ? c : null;
  })();

  const sourceListings = Array.isArray(listings)
    ? listings
    : (Array.isArray(hydrated) ? hydrated : null);

  const effectiveListings = Array.isArray(sourceListings)
    ? sourceListings.filter((it) => {
        const rawType = String(it?.listingType || it?.type || '').toLowerCase();
        return rawType === 'wanted';
      })
    : null;

  const isLoading = !Array.isArray(listings) && !Array.isArray(hydrated);

  useEffect(() => {
    if (!cacheKey) return;
    if (!Array.isArray(listings)) return;

    if (listings.length) {
      cacheSet(cacheKey, listings);
      return;
    }

    const existing = cacheGetStale(cacheKey, TTL_24H);
    if (!existing) cacheSet(cacheKey, []);
  }, [cacheKey, listings]);

  useEffect(() => {
    if (!cacheKey) return;
    if (!Array.isArray(sourceListings)) return;

    if (sourceListings.length) {
      cacheSet(cacheKey, sourceListings);
    } else {
      const existing = cacheGetStale(cacheKey, TTL_24H);
      if (!existing) cacheSet(cacheKey, []);
    }
  }, [cacheKey, sourceListings]);

  if (isLoading) {
    return (
      <div className="profile-content-section">
        <div className="profile-feed feed profile-feed--skeleton">
          {Array.from({ length: 6 }).map((_, idx) => (
            <div key={idx} className="ad-card ig-card ad-card--skeleton search-card--skeleton">
              <div className="ad-thumb ig-thumb" />
              <div className="search-card__badge-skeleton" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (!effectiveListings || effectiveListings.length === 0) {
    return (
      <div className="profile-content-section">
        <div className="empty-state" role="status" aria-live="polite">
          <img className="empty-state__img" src={emptyIllustration} alt="" />
          <p className="empty-state__text">Поки що немає запитів</p>
        </div>
      </div>
    );
  }

  return (
    <div className="profile-content-section">
      <div className="ads-grid ig-grid search-grid">
        {effectiveListings.map((ad) => {
          const id = ad.listingId || ad.id;
          const title = String(ad?.title || 'Шукаю').trim();
          const isDeep = String(id) === String(deepItem);
          const isUnderReview = !!ad.__autoHidden && isStaff;
          const posterTheme = getStableTheme(ad, title);
          const titleSizeClass = getTitleSizeClass(title);

          return (
            <button
              key={id}
              className={`ad-card ig-card search-card ${isDeep ? 'pulse-highlight' : ''}`}
              onClick={() => onOpenListing?.(id)}
              aria-label={`Відкрити запит: ${title}`}
              data-ad-id={id}
            >
              <div className="ad-thumb ig-thumb search-thumb">
                {ad.mainPhoto ? (
                  <img
                    {...thumbImgProps(ad.mainPhoto, "(max-width: 600px) 34vw, 220px")}
                    alt={title}
                    onError={fallbackToOriginal(ad.mainPhoto, DEFAULT_AVATAR_URL)}
                  />
                ) : (
                  <div className={`search-thumb__poster search-thumb__poster--${posterTheme}`}>
                    <div className="search-thumb__poster-glow" />
                    <div className={`search-thumb__poster-title ${titleSizeClass}`}>
                      {title}
                    </div>
                  </div>
                )}

                {isUnderReview && (
                  <div className="ad-review-overlay">
                    <div className="ad-review-badge">Приховано через скарги</div>
                    <div className="ad-review-text">Очікує рішення модератора</div>
                  </div>
                )}
              </div>

              <div className="search-meta">
                <div className="search-title">{title}</div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}